import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  makeStyles
} from "@material-ui/core";
import { BrandGroupsDetails_brandGroup } from "@saleor/brands/types/BrandGroupsDetails";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import ControlledSwitch from "@saleor/components/ControlledSwitch";
import Form from "@saleor/components/Form";
import FormSpacer from "@saleor/components/FormSpacer";
import Grid from "@saleor/components/Grid";
import { MultiAutocompleteChoiceType } from "@saleor/components/MultiAutocompleteSelectField";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import { BrandErrorFragment } from "@saleor/fragments/types/BrandErrorFragment";
import { maybe } from "@saleor/misc";
import { getChoices } from "@saleor/products/utils/data";
import { SearchBrandTags_search_edges_node } from "@saleor/searches/types/SearchBrandTags";
import { FetchMoreProps } from "@saleor/types";
import { BrandVerticalEnum } from "@saleor/types/globalTypes";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import BrandGroupDeepLink from "../BrandGroupDeeplink";
import BrandGroupImage from "../BrandGroupImage";
import BrandGroupInfo from "../BrandGroupInfo/BrandGroupInfo";
import BrandGroupSearchTags from "../BrandGroupSearchTags";

const useStyles = makeStyles(
  theme => ({
    breadcrumbs: {
      [theme.breakpoints.down("sm")]: {
        fontSize: 20,
        padding: 0
      },
      alignSelf: "flex-start",
      flex: 1,
      fontSize: 20,
      paddingBottom: "10px"
    },
    frmAction: {
      justifyContent: "flex-end"
    },
    frmBtn: {
      "&.active": {
        "& span": {
          color: "#fff"
        },
        backgroundColor: "#285BAD"
      },
      "&:hover": {
        "& span": {
          color: "#fff"
        },
        backgroundColor: "#285BAD"
      },
      color: theme.palette.primary.main,
      padding: "6px 30px"
    },
    frmTxt: {
      alignItems: "center",
      display: "flex",
      flexDirection: "column",
      fontSize: "18px",
      justifyContent: "center",
      minHeight: "100px"
    }
  }),
  { name: "BrandGroupDetailsPage" }
);
export interface BrandGroupFormData {
  id: string;
  name: string;
  classification: string;
  vertical: BrandVerticalEnum;
  code: string;
  image: {
    alt: string;
    url: string;
  };
  imageAlt: string;
  isSmacAffiliate?: boolean;
  tags: MultiAutocompleteChoiceType[];
  // tags: any[];
}

export interface BrandGroupDetailsPageProps {
  onFileSizeError: () => void;
  onIsSmacAffiliate: (isSmacAffiliate: boolean) => void;
  errors: BrandErrorFragment[];
  onDelete: () => void;
  onBack: () => void;
  disabled: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  brandGroup: BrandGroupsDetails_brandGroup;
  onIconUpload: (file: File) => void;
  tags: SearchBrandTags_search_edges_node[];
  fetchMoreTags: FetchMoreProps;
  fetchTags: (query: string) => void;
  onSubmit(data: BrandGroupFormData);
}

const BrandGroupDetailsPage: React.FC<BrandGroupDetailsPageProps> = props => {
  const {
    onIconUpload,
    onDelete,
    errors,
    onBack,
    tags,
    fetchTags,
    fetchMoreTags,
    saveButtonBarState,
    brandGroup,
    disabled,
    onSubmit,
    onFileSizeError
  } = props;
  const classes = useStyles("");
  const intl = useIntl();
  const [title, setTitle] = useState("");
  const [open, setOpen] = useState(false);
  const tagsChoices = getChoices(tags);

  const initialForm: BrandGroupFormData = {
    classification: brandGroup?.classification || null,
    code: brandGroup?.code || "",
    id: brandGroup?.id || "",
    image: {
      alt: maybe(() => brandGroup.image.alt, ""),
      url: maybe(() => brandGroup.image.url, "")
    },
    imageAlt: maybe(() => brandGroup.image.alt),
    isSmacAffiliate: brandGroup?.isSmacAffiliate || false,
    name: brandGroup?.name || "",
    tags: maybe(
      () =>
        brandGroup.tags.map(tag => ({
          label: tag.name,
          value: tag.id
        })),
      []
    ),
    vertical: brandGroup?.vertical || null
  };
  const callback = (data, click) => {
    if (click) {
      onSubmit({
        ...data
      });
    }
    setOpen(false);
  };

  const dialogConfirm = message => {
    setTitle(message);
    setOpen(true);
  };

  const handleSubmit = () => {
    dialogConfirm("Are you sure you want to save this BrandGroup?");
  };

  return (
    <>
      <Form initial={initialForm} onSubmit={handleSubmit}>
        {({ data, change, hasChanged, submit }) => {
          const breadcrumbDirectory = [
            {
              name: "Brands",
              url: "/brands"
            },
            {
              name: data && data.name
            }
          ];
          return (
            <Container>
              <PageBreadcrumbNavigation items={breadcrumbDirectory} />
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  marginTop: "10px"
                }}
              >
                <PageBackNavigation onBack={onBack} />
                <div>
                  <PageHeader title={data && data.name} />
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    marginLeft: "auto"
                  }}
                >
                  <PageHeader>
                    <ControlledSwitch
                      checked={data.isSmacAffiliate}
                      disabled={disabled}
                      label={intl.formatMessage({
                        defaultMessage: "SMAC Affiliates",
                        description: "switch button"
                      })}
                      name="isSmacAffiliate"
                      onChange={change}
                    />
                  </PageHeader>
                </div>
              </div>

              <Grid>
                <div>
                  <BrandGroupInfo
                    data={data}
                    onChange={change}
                    disabled={false}
                    errors={errors}
                  />
                  <FormSpacer />
                </div>
                <div>
                  <BrandGroupSearchTags
                    data={data}
                    onChange={change}
                    errors={errors}
                    tags={tagsChoices}
                    fetchTags={fetchTags}
                    fetchMoreTags={fetchMoreTags}
                  />
                  <FormSpacer />
                  <BrandGroupImage
                    data={data}
                    onIconUpload={file => onIconUpload(file)}
                    errors={errors}
                    disabled={false}
                    onChange={change}
                    onFileSizeError={onFileSizeError}
                  />
                  <FormSpacer />
                  <BrandGroupDeepLink data={data} />
                </div>
              </Grid>
              <SaveButtonBar
                disabled={disabled || !hasChanged}
                onCancel={onBack}
                onDelete={onDelete}
                onSave={submit}
                state={saveButtonBarState}
              />
              <Dialog
                open={open}
                onClose={() => setOpen(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
              >
                <DialogContent className={classes.frmTxt}>
                  {title}
                </DialogContent>
                <DialogActions className={classes.frmAction}>
                  <Button
                    className={classes.frmBtn}
                    id="btnNo"
                    color="primary"
                    type="button"
                    onClick={() => callback(data, false)}
                  >
                    No
                  </Button>
                  <Button
                    className={classes.frmBtn + " active"}
                    id="btnYes"
                    color="primary"
                    type="button"
                    onClick={() => callback(data, true)}
                  >
                    Yes
                  </Button>
                </DialogActions>
              </Dialog>
            </Container>
          );
        }}
      </Form>
    </>
  );
};

export default BrandGroupDetailsPage;
